import { CustomError, PageEnvelope } from "@skv/core";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { useRootView } from "./RootView";

export const getPageData = <T extends any>(
  url: string,
  options?: RequestInit
) => {
  return fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    ...options,
  })
    .then<PageEnvelope<T>>((res) => {
      if (res.ok) {
        return res.json();
      }

      throw Error("Unhandled network error");
    })
    .then(({ data, success, errorCode, meta }) => {
      if (success) {
        return data;
      }

      throw new CustomError(`Error from server: ${errorCode}`, errorCode, meta);
    });
};

type FetchStates = "idle" | "loaded" | "loading" | "error";

interface UseViewModelHookOptions<ViewModel> {
  url?: string;
  onClientLoad?: (data: ViewModel) => void;
  cache?: boolean;
  noFetch?: boolean;
}

export const useViewModel = <ViewModel extends any>(
  options?: UseViewModelHookOptions<ViewModel>
) => {
  const [rootState, rootDispatch, rootGetState] = useRootView();
  const location = useLocation();

  const { url, onClientLoad, cache = true, noFetch = false } = options || {};

  const onClientLoadRef = React.useRef(onClientLoad);

  const _url =
    typeof url === "undefined" ? location.pathname + location.search : url;

  const status: FetchStates = (() => {
    if (noFetch) {
      return "idle";
    }

    if (rootState.cache[_url]) {
      if (rootState.cache[_url] instanceof Error) {
        return "error";
      }

      return "loaded";
    }
    return "loading";
  })();

  // todo : maybe look at setting this to null instead of CustomError (potentially breaking)
  const data: ViewModel = rootState.cache[_url];

  const error: CustomError | Error =
    status === "error" ? rootState.cache[_url] : null;

  const updateViewModel = React.useMemo(() => {
    function updateViewModel(data: ViewModel): ViewModel;
    function updateViewModel(
      callback: (data: ViewModel) => ViewModel
    ): ViewModel;
    function updateViewModel(data: any): ViewModel {
      if (typeof data === "function") {
        const nextState = data(rootGetState().cache[_url]);
        rootDispatch((dispatch, getState) => {
          dispatch({
            type: "UPDATE_CACHED_VIEW",
            payload: {
              url: _url,
              data: nextState,
            },
          });
        });
        return nextState;
      }

      rootDispatch({
        type: "UPDATE_CACHED_VIEW",
        payload: {
          url: _url,
          data,
        },
      });
      return data;
    }

    return updateViewModel;
  }, [rootDispatch, rootGetState, _url]);

  const getViewModel = React.useMemo(() => {
    return () =>
      new Promise<ViewModel>((resolve, reject) => {
        rootDispatch((dispatch, getState) => {
          getPageData<ViewModel>(_url)
            .then((data) => {
              dispatch({
                type: "ADD_VIEW_TO_CACHE",
                payload: {
                  url: _url,
                  data,
                },
              });

              if (onClientLoadRef.current) {
                onClientLoadRef.current(data);
              }

              resolve(data);
            })
            .catch((err: Error | CustomError) => {
              dispatch({
                type: "URL_VIEW_MODEL_ERROR",
                payload: {
                  url: _url,
                  error: err,
                },
              });

              reject(err);
            });
        });
      });
  }, [_url, onClientLoadRef, rootDispatch]);

  React.useEffect(() => {
    rootDispatch((dispatch, getState) => {
      if (getState().cache[_url]) {
        return getState().cache[_url];
      }

      if (noFetch === false) {
        getViewModel();
      }
    });

    return () => {
      if (cache === false) {
        rootDispatch({
          type: "REMOVE_CACHED_VIEW",
          payload: {
            url: _url,
          },
        });
      }
    };
  }, [_url, rootDispatch, noFetch, cache, getViewModel]);

  return [
    { data, status, error },
    { updateViewModel, getViewModel },
  ] as const;
};
